import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from "@angular/forms";
import { SearchFormHelperService } from "app_sasha/shared/services/search-form-helper/search-form-helper.service";
import { Yii } from "libraries/interfaces/Yii";
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { INumberFieldSpecs } from "libraries/module/formbuilder/fields/number/INumberFieldSpecs";
import { IDateRangeFieldSpecs } from "libraries/module/formbuilder/fields/date-range/date-range.component";
import { IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { TicketSearchFormMapperService } from "../ticketing-search-form-mapper.service";
import { TicketingReportSearchTicket } from "@afidium/ts-facade";
import {
    IDescriptionLabels
} from "libraries/module/formbuilder/fields/form-field/components/field-description/field-description.component";
import { PAYMENT_OPTIONS } from "libraries/enums/payment-options";

enum ETicketSearchTicketFields {
    systems = 'system',
    types = 'types',
    numbers = 'numbers',
    agencies = 'agencies',
    agents = 'agents',
    travellers = 'travellers',
    amount = 'amount',
    amountMin = 'amountMin',
    amountMax = 'amountMax',
    commission = 'commission',
    commissionMin = 'commissionMin',
    commissionMax = 'commissionMax',
    emission = 'emission',
    emissionMin = 'emissionMin',
    emissionMax = 'emissionMax',
    payment = 'payment',
    vendors = 'vendors',
}

export interface ITicketSearchFormTicketOutput {
    [ETicketSearchTicketFields.systems]: string[];
    [ETicketSearchTicketFields.types]: string[];
    [ETicketSearchTicketFields.numbers]: string[];
    [ETicketSearchTicketFields.agencies]: string[];
    [ETicketSearchTicketFields.agents]: string[];
    [ETicketSearchTicketFields.travellers]: string[];
    [ETicketSearchTicketFields.amount]: {
        [ETicketSearchTicketFields.amountMin]: number;
        [ETicketSearchTicketFields.amountMax]: number;
    };
    [ETicketSearchTicketFields.commission]: {
        [ETicketSearchTicketFields.commissionMin]: number;
        [ETicketSearchTicketFields.commissionMax]: number;
    };
    [ETicketSearchTicketFields.emission]: {
        [ETicketSearchTicketFields.emissionMin]: Date;
        [ETicketSearchTicketFields.emissionMax]: Date;
    };
    [ETicketSearchTicketFields.payment]: string[];
    [ETicketSearchTicketFields.vendors]: string[];
}

interface ITicketConfig {
    [ETicketSearchTicketFields.systems]: IFieldSpecs;
    systemOptions?: IMultiselectOptions[];
    typeOptions?: IMultiselectOptions[];
    paymentOptions?: IMultiselectOptions[];
    [ETicketSearchTicketFields.types]: IFieldSpecs;
    [ETicketSearchTicketFields.numbers]: IFieldSpecs;
    [ETicketSearchTicketFields.agencies]: IFieldSpecs;
    [ETicketSearchTicketFields.agents]: IFieldSpecs;
    [ETicketSearchTicketFields.travellers]: IFieldSpecs;
    [ETicketSearchTicketFields.amountMin]: INumberFieldSpecs;
    [ETicketSearchTicketFields.amountMax]: INumberFieldSpecs;
    [ETicketSearchTicketFields.commissionMin]: INumberFieldSpecs;
    [ETicketSearchTicketFields.commissionMax]: INumberFieldSpecs;
    [ETicketSearchTicketFields.emission]: IDateRangeFieldSpecs;
    [ETicketSearchTicketFields.payment]: IFieldSpecs;
    [ETicketSearchTicketFields.vendors]: IFieldSpecs;
}

class Wording {
    public readonly tickets: string = Yii.t('wui-sasha-js', 'Tickets');
    public readonly [ETicketSearchTicketFields.systems]: string = Yii.t('wui-sasha-js', 'Systèmes');
    public readonly [ETicketSearchTicketFields.types]: string = Yii.t('wui-sasha-js', 'Types');
    public readonly [ETicketSearchTicketFields.numbers]: string = Yii.t('wui-sasha-js', 'Numéros');
    public readonly [ETicketSearchTicketFields.agencies]: string = Yii.t('wui-sasha-js', 'Agences');
    public readonly [ETicketSearchTicketFields.agents]: string = Yii.t('wui-sasha-js', 'Agents');
    public readonly [ETicketSearchTicketFields.travellers]: string = Yii.t('wui-sasha-js', 'Voyageurs');
    public readonly [ETicketSearchTicketFields.amountMin]: string = Yii.t('wui-sasha-js', 'Montant min');
    public readonly [ETicketSearchTicketFields.amountMax]: string = Yii.t('wui-sasha-js', 'Montant max');
    public readonly [ETicketSearchTicketFields.commissionMin]: string = Yii.t('wui-sasha-js', 'Commission min');
    public readonly [ETicketSearchTicketFields.commissionMax]: string = Yii.t('wui-sasha-js', 'Commission max');
    public readonly [ETicketSearchTicketFields.payment]: string = Yii.t('wui-sasha-js', 'Forme de paiement');
    public readonly [ETicketSearchTicketFields.emissionMin]: string = Yii.t('wui-sasha-js', 'Emission min');
    public readonly [ETicketSearchTicketFields.emissionMax]: string = Yii.t('wui-sasha-js', 'Emission max');
    public readonly [ETicketSearchTicketFields.vendors]: string = Yii.t('wui-sasha-js', 'Compagnies émettrices');

    public readonly syntaxHelp: string[] = [
        Yii.t('wui-sasha-js','NOT ou un tiret au début d\'un mot pour rechercher des valeurs qui ne contiennent pas ce mot ;'),
        Yii.t('wui-sasha-js','* pour rechercher un texte partiel ;'),
        Yii.t('wui-sasha-js','AND pour combiner des expressions.'),
    ];
    public readonly systemDescription: IDescriptionLabels = {
        main : Yii.t('wui-sasha-js', 'Système depuis lesquels les billets ont été traités.'),
    };
    public readonly vendorDescription: IDescriptionLabels = {
        main : Yii.t('wui-sasha-js', 'Pour rechercher sur les compagnies aériennes chez qui les billets ont été émis. La recherche s\'effectue par code IATA uniquement.'),
        syntaxHelp: this.syntaxHelp
    };
    public readonly typeDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js', 'Pour recherche sur les types de billets (Billet Electronique et/ou EMD).')
    };
    public readonly numberDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Permet de rechercher des numéros de billets.'),
        syntaxHelp: this.syntaxHelp
    };
    public readonly agencyDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Champ permettant de rechercher sur le code GDS des agences associées au dossier du billet.'),
        syntaxHelp: this.syntaxHelp
    };
    public readonly agentDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Champ de recherche sur le titulaire du dossier associé au billet.'),
        detailIntroduction: Yii.t('wui-sasha-js', 'Ce champ cherche dans les dossiers les informations suivantes :'),
        detailList: [
            Yii.t('wui-sasha-js', 'noms d\'agents ;'),
            Yii.t('wui-sasha-js', 'références d\'agents ;'),
            Yii.t('wui-sasha-js', 'numéros de télephone d\'agents ;'),
            Yii.t('wui-sasha-js', 'adresses email d\'agents.'),
        ],
        syntaxHelp: this.syntaxHelp
    };
    public readonly travellerDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Champ de recherche de noms de voyageur.'),
        syntaxHelp: this.syntaxHelp
    };
    public readonly amountMinDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Montant total minimum des billets à rechercher.'),
    };
    public readonly amountMaxDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Montant total maximum des billets à rechercher.'),
    };
    public readonly commissionMinDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Montant minimum  de la commission sur les billets à rechercher.'),
    };
    public readonly commissionMaxDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Montant maximum  de la commission sur les billets à rechercher.'),
    };
    public readonly paymentDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Pour rechercher sur les formes de paiement utilisées pour émettre les billets.'),
    };
    public readonly dateDetails: string[] = [
        Yii.t('wui-sasha-js', 'aujourd\'hui : = ;'),
        Yii.t('wui-sasha-js', 'début du mois : < ;'),
        Yii.t('wui-sasha-js', 'fin du mois : > ;'),
        Yii.t('wui-sasha-js', 'dans deux semaines : +2w ;'),
        Yii.t('wui-sasha-js', 'dans trois mois : +3m .'),
    ];
    public readonly emissionMinDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Pour indiquer la date à partir de laquelle les billets ont été émis.'),
        detailIntroduction: Yii.t('wui-sasha-js', 'Ce champ supporte les expressions de date :'),
        detailList: this.dateDetails,
    };
    public readonly emissionMaxDescription: IDescriptionLabels = {
        main: Yii.t('wui-sasha-js','Pour indiquer la date jusqu\'à laquelle les billets ont été émis.'),
        detailIntroduction: this.emissionMinDescription.detailIntroduction,
        detailList: this.dateDetails,
    };

}

@Component({
    selector: 'ticketing-ticket-fieldset',
    template: `
        <collapsible [title]="this.wording.tickets" [open]="ticketOpen">
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <multiselect [apiOptions]="ticketConfig.systemOptions"
                                 [fieldConfig]="ticketConfig[ticketField.systems]"></multiselect>
                </div>
                <div class="flex-1">
                    <chips [fieldConfig]="ticketConfig[ticketField.vendors]" [toUpper]="true"></chips>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <multiselect [apiOptions]="ticketConfig.typeOptions"
                                 [fieldConfig]="ticketConfig[ticketField.types]"></multiselect>
                </div>
                <div class="flex-1">
                    <chips [fieldConfig]="ticketConfig[ticketField.numbers]"></chips>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1 sep-right">
                    <chips [fieldConfig]="ticketConfig[ticketField.agencies]"></chips>
                </div>
                <div class="flex-1">
                    <chips [fieldConfig]="ticketConfig[ticketField.agents]"></chips>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1">
                    <chips [fieldConfig]="ticketConfig[ticketField.travellers]"></chips>
                </div>
            </div>
            <div class="col-to-row-s-tb sep-top">
                <div class="col-to-row-s-tb flex-1 sep-right">
                    <div class="flex-1 sep-right">
                        <number [fieldConfig]="ticketConfig[ticketField.amountMin]"></number>
                    </div>
                    <div class="flex-1">
                        <number [fieldConfig]="ticketConfig[ticketField.amountMax]"></number>
                    </div>
                </div>
                <div class="col-to-row-s-tb flex-1">
                    <div class="flex-1 sep-right">
                        <number [fieldConfig]="ticketConfig[ticketField.commissionMin]"></number>
                    </div>
                    <div class="flex-1">
                        <number [fieldConfig]="ticketConfig[ticketField.commissionMax]"></number>
                    </div>
                </div>

            </div>

            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1">
                    <date-range-field [formGroup]="myFormGroup.controls[ticketField.emission]"
                                      [fieldConfig]="ticketConfig[ticketField.emission]" ></date-range-field>
                </div>
            </div>

            <div class="col-to-row-s-tb sep-top">
                <div class="flex-1">
                    <multiselect [apiOptions]="ticketConfig.paymentOptions"
                                 [fieldConfig]="ticketConfig[ticketField.payment]"></multiselect>
                </div>
            </div>

        </collapsible>
    `,
    styles: []
})
export class TicketingTicketFieldsetComponent implements OnInit {

    @Input()
    public myFormGroup: FormGroup;

    @Input()
    public apiRequestTicket: TicketingReportSearchTicket;

    public readonly ticketField: typeof ETicketSearchTicketFields = ETicketSearchTicketFields;
    public readonly wording: Wording = new Wording();

    public ticketOpen: boolean = true;

    public ticketConfig: ITicketConfig;

    constructor(
        private readonly helperService: SearchFormHelperService,
        private readonly ticketSearchFormMapperService: TicketSearchFormMapperService) {
    }

    public ngOnInit(): void {
        this.createTicketFormControls();
        this.ticketConfig = this.createTicketConfig();
        this.patchForm();
        this.helperService.getHosts()
            .subscribe({
                next: (host: IMultiselectOptions[]): void => {
                    this.ticketConfig.systemOptions = host;
                }
            });
    }
    public patchForm(): void {
        if (!this.apiRequestTicket) {
            this.myFormGroup.reset({});
        }else {
            this.myFormGroup.setValue(this.ticketSearchFormMapperService.mapApiTicketToFormTicket(this.apiRequestTicket));
        }
    }

    public createTicketFormControls(): void {
        this.myFormGroup.setControl(ETicketSearchTicketFields.systems, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.vendors, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.types, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.numbers, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.agencies, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.agents, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.travellers, new FormControl());
        this.myFormGroup.setControl(ETicketSearchTicketFields.amount, new FormGroup({
            [ETicketSearchTicketFields.amountMin]: new FormControl(),
            [ETicketSearchTicketFields.amountMax]: new FormControl(),
        }));
        this.myFormGroup.setControl(ETicketSearchTicketFields.commission, new FormGroup({
            [ETicketSearchTicketFields.commissionMin]: new FormControl(),
            [ETicketSearchTicketFields.commissionMax]: new FormControl(),
        }));
        this.myFormGroup.setControl(ETicketSearchTicketFields.emission, new FormGroup({
            [ETicketSearchTicketFields.emissionMin]: new FormControl(),
            [ETicketSearchTicketFields.emissionMax]: new FormControl(),
        }));
        this.myFormGroup.setControl(ETicketSearchTicketFields.payment, new FormControl());
    }

    public createTicketConfig(): ITicketConfig {
        return {
            [ETicketSearchTicketFields.systems]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.systems,
                this.myFormGroup,
                this.wording.system,
                this.wording.systemDescription),
            [ETicketSearchTicketFields.vendors]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.vendors,
                this.myFormGroup,
                this.wording.vendors,
                this.wording.vendorDescription),
            [ETicketSearchTicketFields.types]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.types,
                this.myFormGroup,
                this.wording.types,
                this.wording.typeDescription),
            [ETicketSearchTicketFields.numbers]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.numbers,
                this.myFormGroup,
                this.wording.numbers,
                this.wording.numberDescription),
            [ETicketSearchTicketFields.agencies]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.agencies,
                this.myFormGroup,
                this.wording.agencies,
                this.wording.agencyDescription),
            [ETicketSearchTicketFields.agents]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.agents,
                this.myFormGroup,
                this.wording.agents,
                this.wording.agentDescription),
            [ETicketSearchTicketFields.travellers]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.travellers,
                this.myFormGroup,
                this.wording.travellers,
                this.wording.travellerDescription),
            [ETicketSearchTicketFields.amountMin]: this.helperService.createNumberConfig(
                ETicketSearchTicketFields.amountMin,
                this.myFormGroup.get(ETicketSearchTicketFields.amount) as FormGroup,
                this.wording.amountMin,
                this.wording.amountMinDescription),
            [ETicketSearchTicketFields.amountMax]: this.helperService.createNumberConfig(
                ETicketSearchTicketFields.amountMax,
                this.myFormGroup.get(ETicketSearchTicketFields.amount) as FormGroup,
                this.wording.amountMax,
                this.wording.amountMaxDescription),
            [ETicketSearchTicketFields.commissionMin]: this.helperService.createNumberConfig(
                ETicketSearchTicketFields.commissionMin,
                this.myFormGroup.get(ETicketSearchTicketFields.commission) as FormGroup,
                this.wording.commissionMin,
                this.wording.commissionMinDescription),
            [ETicketSearchTicketFields.commissionMax]: this.helperService.createNumberConfig(
                ETicketSearchTicketFields.commissionMax,
                this.myFormGroup.get(ETicketSearchTicketFields.commission) as FormGroup,
                this.wording.commissionMax,
                this.wording.commissionMaxDescription),
            [ETicketSearchTicketFields.emission]: {
                [ETicketSearchTicketFields.emissionMin]: this.helperService.createDateConfig(
                    ETicketSearchTicketFields.emissionMin,
                    this.myFormGroup.get(ETicketSearchTicketFields.emission) as FormGroup,
                    this.wording.emissionMin,
                    true,
                    this.wording.emissionMinDescription),
                [ETicketSearchTicketFields.emissionMax]: this.helperService.createDateConfig(
                    ETicketSearchTicketFields.emissionMax,
                    this.myFormGroup.get(ETicketSearchTicketFields.emission) as FormGroup,
                    this.wording.emissionMax,
                    true,
                    this.wording.emissionMaxDescription),
            }as IDateRangeFieldSpecs,
            [ETicketSearchTicketFields.payment]: this.helperService.createSimpleConfig(
                ETicketSearchTicketFields.payment,
                this.myFormGroup,
                this.wording.payment,
                this.wording.paymentDescription),
            typeOptions: [
                {
                    value: 'eticket',
                    label: Yii.t('wui-sasha-js', 'Billet électronique')
                },
                {
                    value: 'EMD',
                    label: Yii.t('wui-sasha-js', 'EMD')
                }
            ],
            paymentOptions: PAYMENT_OPTIONS,
        };
    }
}
